import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ChevronRight, ChevronDown } from 'lucide-react'
import { cn } from '@/utils/cn'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { CriteriaBuilder } from '@/components/StoreModels/CriteriaBuilder'
import {
  DEFAULT_CRITERIA,
  buildProcessedCriteria,
} from '@/components/StoreModels/criteriaUtils'
import { useCreateStoreModel } from '@/hooks/useStoreModels'
import { toast } from '@/stores/useToastStore'
import type { AxiosError } from 'axios'
import type { ModelType, AllocationBasis, StrongModelCriteria } from '@/types'

const formSchema = z.object({
  name: z.string().min(1, 'Name is required').max(255),
  model_type: z.enum(['STATIC', 'DYNAMIC'] as const),
  allocation_basis: z.enum(['UNITS', 'SALES'] as const),
  description: z.string().max(1000).optional().or(z.literal('')),
  recalculation_schedule: z.string().optional().or(z.literal('')),
})

type FormValues = z.infer<typeof formSchema>

export default function AdminStoreModelCreate() {
  const navigate = useNavigate()
  const createMutation = useCreateStoreModel()

  const [criteria, setCriteria] =
    useState<StrongModelCriteria>(DEFAULT_CRITERIA)
  const [advancedOpen, setAdvancedOpen] = useState(false)

  const isFormDisabled = createMutation.isPending

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      model_type: 'STATIC',
      allocation_basis: 'UNITS',
      description: '',
      recalculation_schedule: '',
    },
  })

  const currentModelType = watch('model_type')
  const currentAllocationBasis = watch('allocation_basis')

  const onSubmit = (data: FormValues) => {
    const processedCriteria = buildProcessedCriteria(criteria)

    createMutation.mutate(
      {
        name: data.name,
        model_type: data.model_type,
        criteria: processedCriteria,
        description: data.description || null,
        allocation_basis: data.allocation_basis,
        recalculation_schedule: data.recalculation_schedule || null,
      },
      {
        onSuccess: response => {
          navigate(`/store-models/${response.resource_id}`, {
            state: { processingResponse: response },
          })
        },
        onError: error => {
          const axiosError = error as AxiosError<{ detail?: string }>
          const message =
            axiosError.response?.data?.detail ?? 'Failed to create store model'
          toast.error(message)
        },
      }
    )
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1 text-sm text-muted-foreground">
        <Link
          to="/store-models"
          className="hover:text-foreground transition-colors cursor-pointer"
        >
          Store Models
        </Link>
        <ChevronRight className="h-4 w-4" />
        <span className="text-foreground font-medium">Create Model</span>
      </nav>

      <h1 className="text-2xl font-semibold text-foreground">
        Create Store Model
      </h1>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-6">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  placeholder="Enter model name"
                  disabled={isFormDisabled}
                  {...register('name')}
                />
                {errors.name && (
                  <p className="text-sm text-destructive">
                    {errors.name.message}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="model_type">Model Type *</Label>
                <Select
                  value={currentModelType}
                  onValueChange={(v: ModelType) => setValue('model_type', v)}
                  disabled={isFormDisabled}
                >
                  <SelectTrigger id="model_type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="STATIC">Static</SelectItem>
                    <SelectItem value="DYNAMIC">Dynamic</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="allocation_basis">Allocation Basis</Label>
                <Select
                  value={currentAllocationBasis}
                  onValueChange={(v: AllocationBasis) =>
                    setValue('allocation_basis', v)
                  }
                  disabled={isFormDisabled}
                >
                  <SelectTrigger id="allocation_basis">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UNITS">Units</SelectItem>
                    <SelectItem value="SALES">Sales</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Optional description for this model"
                disabled={isFormDisabled}
                {...register('description')}
              />
              {errors.description && (
                <p className="text-sm text-destructive">
                  {errors.description.message}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Advanced Options */}
        <div className="border border-border rounded-lg">
          <button
            type="button"
            onClick={() => setAdvancedOpen(prev => !prev)}
            className="flex w-full items-center gap-2 px-4 py-3 text-sm font-medium text-foreground hover:bg-muted/50 transition-colors cursor-pointer"
          >
            {advancedOpen ? (
              <ChevronDown className="h-4 w-4 shrink-0" />
            ) : (
              <ChevronRight className="h-4 w-4 shrink-0" />
            )}
            Advanced Options
          </button>
          <div
            className={cn(
              'overflow-hidden transition-[max-height] duration-200 ease-in-out',
              advancedOpen ? 'max-h-[200px]' : 'max-h-0'
            )}
          >
            <div className="px-4 pb-4 flex flex-col gap-1.5">
              <Label htmlFor="recalculation_schedule">
                Recalculation Schedule
              </Label>
              <Input
                id="recalculation_schedule"
                placeholder="e.g. 0 0 * * MON"
                disabled={isFormDisabled}
                {...register('recalculation_schedule')}
              />
            </div>
          </div>
        </div>

        {/* Criteria */}
        <Card>
          <CardHeader>
            <CardTitle>Criteria</CardTitle>
          </CardHeader>
          <CardContent>
            <CriteriaBuilder
              criteria={criteria}
              onChange={setCriteria}
              disabled={isFormDisabled}
            />
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            className="cursor-pointer"
            disabled={isFormDisabled}
            onClick={() => navigate('/store-models')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="cursor-pointer"
            disabled={isFormDisabled}
          >
            {createMutation.isPending ? 'Creating...' : 'Create Model'}
          </Button>
        </div>
      </form>
    </div>
  )
}
